import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, inputCls, inputClsError } from "@/dashboard/ui";
import { Building2, ImagePlus, Plus, Trash2, XCircle } from "lucide-react";
import PhoneInput, { phoneIsValid } from "@/components/PhoneInput";
import Autocomplete, { INDUSTRIES, COUNTRIES, loadCities, suggestTimezone, canonicalTimezone } from "@/dashboard/Autocomplete";
import timezonesData from "@/data/timezones.json";

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const TONES = ["Warm and friendly", "Professional", "Casual", "Luxury", "Energetic"];

const EMPTY = {
  business_name: "", industry: "", additional_info: "", business_email: "", business_phone: "",
  website: "", logo_media_id: "", country: "", city: "", address: "", postal_code: "", timezone: "",
  location: "", opening_hours: { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] },
  services_detail: [], typical_duration_min: 0, primary_color: "#111111", secondary_color: "#6b5ce7",
  brand_voice: "Warm and friendly", ai_intro: "", ai_faqs: [], ai_instructions: "", escalation_phone: "",
};

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-neutral-600">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
      {hint && <span className="mt-0.5 block text-[11px] text-neutral-400">{hint}</span>}
    </label>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-neutral-100 pt-4">
      <p className="mb-3 text-xs font-bold uppercase tracking-wider text-neutral-500">{title}</p>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

export default function BusinessProfileCard() {
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    if (!form) return;
    const code = COUNTRIES.find((c) => c.name === form.country)?.code;
    loadCities(code).then(setCityOptions).catch(() => setCityOptions([]));
  }, [form?.country]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: null })); };
  const dirty = form && original && JSON.stringify(form) !== JSON.stringify(original);

  useEffect(() => {
    api.get("/business/profile").then((r) => {
      const p = { ...EMPTY, ...r.data.profile, opening_hours: { ...EMPTY.opening_hours, ...(r.data.profile.opening_hours || {}) } };
      setForm(p);
      setOriginal(p);
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    let objectUrl = null;
    if (form?.logo_media_id) {
      api.get(`/marketing/media/${form.logo_media_id}`, { responseType: "blob" })
        .then((r) => { objectUrl = URL.createObjectURL(r.data); setLogoUrl(objectUrl); })
        .catch(() => setLogoUrl(null));
    } else setLogoUrl(null);
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [form?.logo_media_id]);

  const uploadLogo = async (file) => {
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/marketing/media", fd);
      if (data.kind !== "image") throw new Error("Logo must be an image");
      set("logo_media_id", data.id);
    } catch (e) {
      toast.error(e.message || formatApiError(e));
    } finally {
      setLogoBusy(false);
    }
  };

  const validate = () => {
    const e = {};
    const name = form.business_name.trim();
    if (!name) e.business_name = "Business name is required";
    else if (!/[^\W\d_]/u.test(name)) e.business_name = "Business name must include letters — a number alone is not a valid name";
    if (!form.industry.trim()) e.industry = "Industry is required";
    if (!form.business_email.trim()) e.business_email = "Business email is required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.business_email.trim())) e.business_email = "Enter a valid email";
    if (!form.business_phone.trim()) e.business_phone = "Business phone is required";
    else if (!phoneIsValid(form.business_phone)) e.business_phone = "Enter a valid phone number with country code";
    if (!form.country.trim()) e.country = "Country is required";
    if (!form.timezone.trim()) e.timezone = "Timezone is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/business/profile", { ...form, timezone: canonicalTimezone(form.timezone) });
      const p = { ...EMPTY, ...data.profile, opening_hours: { ...EMPTY.opening_hours, ...(data.profile.opening_hours || {}) } };
      setForm(p);
      setOriginal(p);
      toast.success("Business profile saved — it now feeds your AI agents, landing pages, SEO and marketing");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setDay = (day, ranges) => set("opening_hours", { ...form.opening_hours, [day]: ranges });
  const setSvc = (i, k, v) => set("services_detail", form.services_detail.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const setFaq = (i, k, v) => set("ai_faqs", form.ai_faqs.map((f, j) => (j === i ? { ...f, [k]: v } : f)));

  if (!form) return <Card className="p-6 lg:col-span-2"><p className="py-6 text-center text-sm text-neutral-400">Loading business profile…</p></Card>;

  return (
    <Card className="p-6 lg:col-span-2" data-testid="business-profile-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Building2 size={16} className="text-[#111111]" />
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Business Profile</p>
            <p className="text-[11px] text-neutral-400">Entered once — reused by AI voice agents, appointments, landing pages, SEO and email marketing.</p>
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        <Section title="Basic information">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Business name" required>
              <input className={errors.business_name ? inputClsError : inputCls} value={form.business_name} data-testid="bp-name" onChange={(e) => set("business_name", e.target.value)} />
              {errors.business_name && <span className="mt-0.5 block text-[11px] text-red-600" data-testid="bp-name-error">{errors.business_name}</span>}
            </Field>
            <Field label="Industry / category" required>
              <Autocomplete value={form.industry} onChange={(v) => set("industry", v)} options={INDUSTRIES} placeholder="Start typing, e.g. hair…" testid="bp-industry" />
              {errors.industry && <span className="mt-0.5 block text-[11px] text-red-600">{errors.industry}</span>}
            </Field>
            <Field label="Business email" required>
              <input type="email" className={errors.business_email ? inputClsError : inputCls} value={form.business_email} data-testid="bp-email" onChange={(e) => set("business_email", e.target.value)} />
              {errors.business_email && <span className="mt-0.5 block text-[11px] text-red-600" data-testid="bp-email-error">{errors.business_email}</span>}
            </Field>
            <PhoneInput
              testid="bp-phone"
              label="Business phone"
              required
              value={form.business_phone}
              invalid={!!errors.business_phone}
              error={errors.business_phone}
              onChange={(v) => set("business_phone", v)}
            />
            <Field label="Website URL">
              <input className={inputCls} value={form.website} data-testid="bp-website" placeholder="https://…" onChange={(e) => set("website", e.target.value)} />
            </Field>
            <Field label="Business description">
              <input className={inputCls} value={form.additional_info} data-testid="bp-description" onChange={(e) => set("additional_info", e.target.value)} />
            </Field>
          </div>
        </Section>

        <Section title="Location">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Country" required>
              <Autocomplete value={form.country} placeholder="Start typing, e.g. United…" testid="bp-country"
                options={COUNTRIES.map((c) => c.name)}
                onChange={(v) => {
                  set("country", v);
                  const suggested = suggestTimezone(v);
                  if (suggested && !form.timezone) set("timezone", suggested);
                }} />
              {errors.country && <span className="mt-0.5 block text-[11px] text-red-600">{errors.country}</span>}
            </Field>
            <Field label="City" hint="Suggestions follow the selected country — type any town to use it">
              <Autocomplete value={form.city} onChange={(v) => set("city", v)} options={cityOptions}
                placeholder={form.country ? `Cities in ${form.country}…` : "Pick a country first, or type any city"} testid="bp-city" />
            </Field>
            <Field label="Address">
              <input className={inputCls} value={form.address} data-testid="bp-address" onChange={(e) => set("address", e.target.value)} />
            </Field>
            <Field label="Postal code">
              <input className={inputCls} value={form.postal_code} data-testid="bp-postal" onChange={(e) => set("postal_code", e.target.value)} />
            </Field>
            <Field label="Timezone" required hint="Canonical IANA value — lowercase typing is corrected automatically">
              <Autocomplete value={form.timezone} testid="bp-timezone" placeholder="e.g. Europe/London"
                options={timezonesData} onChange={(v) => set("timezone", canonicalTimezone(v))} />
              {errors.timezone && <span className="mt-0.5 block text-[11px] text-red-600">{errors.timezone}</span>}
            </Field>
            <Field label="Area served">
              <input className={inputCls} value={form.location} data-testid="bp-area" placeholder="e.g. Leeds & Bradford" onChange={(e) => set("location", e.target.value)} />
            </Field>
          </div>
        </Section>

        <Section title="Opening hours">
          <div className="space-y-2" data-testid="bp-hours">
            {DAYS.map(([d, label]) => (
              <div key={d} className="flex flex-wrap items-center gap-2">
                <span className="w-10 text-xs font-semibold text-neutral-600">{label}</span>
                {(form.opening_hours[d] || []).map((r, i) => (
                  <span key={i} className="flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-1">
                    <input type="time" className="bg-transparent text-xs outline-none" value={r.start} data-testid={`bp-hours-${d}-start-${i}`}
                      onChange={(e) => setDay(d, form.opening_hours[d].map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
                    <span className="text-[10px] text-neutral-400">–</span>
                    <input type="time" className="bg-transparent text-xs outline-none" value={r.end} data-testid={`bp-hours-${d}-end-${i}`}
                      onChange={(e) => setDay(d, form.opening_hours[d].map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
                    <button type="button" className="text-neutral-300 hover:text-red-500" data-testid={`bp-hours-${d}-remove-${i}`}
                      onClick={() => setDay(d, form.opening_hours[d].filter((_, j) => j !== i))}><XCircle size={13} /></button>
                  </span>
                ))}
                <button type="button" className="text-[11px] font-semibold text-neutral-400 hover:text-[#111111]" data-testid={`bp-hours-${d}-add`}
                  onClick={() => setDay(d, [...(form.opening_hours[d] || []), { start: "09:00", end: "17:00" }])}>
                  {(form.opening_hours[d] || []).length ? "+ range" : "Set hours"}
                </button>
                {!(form.opening_hours[d] || []).length && <span className="text-[11px] text-neutral-300">Closed</span>}
              </div>
            ))}
          </div>
        </Section>

        <Section title="Services offered">
          <div className="space-y-2" data-testid="bp-services">
            {form.services_detail.map((s, i) => (
              <div key={i} className="grid grid-cols-1 items-start gap-2 rounded-xl border border-neutral-200 p-3 sm:grid-cols-[1fr_110px_110px_32px]">
                <div className="space-y-1.5">
                  <input className={inputCls} placeholder="Service name *" value={s.name} data-testid={`bp-svc-name-${i}`} onChange={(e) => setSvc(i, "name", e.target.value)} />
                  <input className={inputCls} placeholder="Short description" value={s.description} data-testid={`bp-svc-desc-${i}`} onChange={(e) => setSvc(i, "description", e.target.value)} />
                </div>
                <input className={inputCls} placeholder="Price (e.g. £45)" value={s.price} data-testid={`bp-svc-price-${i}`} onChange={(e) => setSvc(i, "price", e.target.value)} />
                <input type="number" min="0" className={inputCls} placeholder="Minutes" value={s.duration_min || ""} data-testid={`bp-svc-dur-${i}`} onChange={(e) => setSvc(i, "duration_min", parseInt(e.target.value) || 0)} />
                <button type="button" className="mt-2 text-neutral-400 hover:text-red-600" data-testid={`bp-svc-remove-${i}`}
                  onClick={() => set("services_detail", form.services_detail.filter((_, j) => j !== i))}><Trash2 size={15} /></button>
              </div>
            ))}
            <Btn type="button" variant="ghost" data-testid="bp-svc-add" disabled={form.services_detail.length >= 50}
              onClick={() => set("services_detail", [...form.services_detail, { name: "", description: "", price: "", duration_min: 30 }])}>
              <Plus size={13} className="mr-1 inline" /> Add service
            </Btn>
          </div>
          <Field label="Typical appointment duration (minutes)">
            <input type="number" min="0" className={inputCls} value={form.typical_duration_min || ""} data-testid="bp-typical-duration"
              onChange={(e) => set("typical_duration_min", parseInt(e.target.value) || 0)} />
          </Field>
        </Section>

        <Section title="Brand">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <span className="mb-1 block text-xs font-semibold text-neutral-600">Logo</span>
              {logoUrl ? (
                <span className="relative inline-block">
                  <img src={logoUrl} alt="Business logo" className="h-16 w-16 rounded-xl border border-neutral-200 object-cover" data-testid="bp-logo-preview" />
                  <button type="button" className="absolute -right-1.5 -top-1.5 rounded-full bg-white text-neutral-400 shadow hover:text-red-600" data-testid="bp-logo-remove"
                    onClick={() => set("logo_media_id", "")}><XCircle size={16} /></button>
                </span>
              ) : (
                <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-neutral-300 px-3.5 py-1.5 text-xs font-semibold text-neutral-600 ${logoBusy ? "opacity-60" : ""}`} data-testid="bp-logo-upload">
                  <ImagePlus size={13} /> {logoBusy ? "Uploading…" : "Upload logo"}
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" disabled={logoBusy}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }} />
                </label>
              )}
            </div>
            <Field label="Primary colour">
              <input type="color" className="h-9 w-16 cursor-pointer rounded-lg border border-neutral-300" value={form.primary_color || "#111111"} data-testid="bp-color-primary" onChange={(e) => set("primary_color", e.target.value)} />
            </Field>
            <Field label="Secondary colour">
              <input type="color" className="h-9 w-16 cursor-pointer rounded-lg border border-neutral-300" value={form.secondary_color || "#6b5ce7"} data-testid="bp-color-secondary" onChange={(e) => set("secondary_color", e.target.value)} />
            </Field>
            <Field label="Communication tone">
              <select className={inputCls} value={form.brand_voice} data-testid="bp-tone" onChange={(e) => set("brand_voice", e.target.value)}>
                {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>
        </Section>

        <Section title="AI receptionist knowledge">
          <Field label="Business introduction" hint="How the AI should introduce your business">
            <textarea rows={2} className={inputCls} value={form.ai_intro} data-testid="bp-ai-intro" onChange={(e) => set("ai_intro", e.target.value)} />
          </Field>
          <div className="space-y-2" data-testid="bp-faqs">
            <span className="block text-xs font-semibold text-neutral-600">Frequently asked questions</span>
            {form.ai_faqs.map((f, i) => (
              <div key={i} className="grid grid-cols-1 items-start gap-2 rounded-xl border border-neutral-200 p-3 sm:grid-cols-[1fr_1fr_32px]">
                <input className={inputCls} placeholder="Question" value={f.q} data-testid={`bp-faq-q-${i}`} onChange={(e) => setFaq(i, "q", e.target.value)} />
                <input className={inputCls} placeholder="Answer" value={f.a} data-testid={`bp-faq-a-${i}`} onChange={(e) => setFaq(i, "a", e.target.value)} />
                <button type="button" className="mt-2 text-neutral-400 hover:text-red-600" data-testid={`bp-faq-remove-${i}`}
                  onClick={() => set("ai_faqs", form.ai_faqs.filter((_, j) => j !== i))}><Trash2 size={15} /></button>
              </div>
            ))}
            <Btn type="button" variant="ghost" data-testid="bp-faq-add" disabled={form.ai_faqs.length >= 30}
              onClick={() => set("ai_faqs", [...form.ai_faqs, { q: "", a: "" }])}>
              <Plus size={13} className="mr-1 inline" /> Add FAQ
            </Btn>
          </div>
          <Field label="Special instructions for the AI receptionist">
            <textarea rows={2} className={inputCls} value={form.ai_instructions} data-testid="bp-ai-instructions"
              placeholder="e.g. Always offer a patch test before colour appointments" onChange={(e) => set("ai_instructions", e.target.value)} />
          </Field>
          <PhoneInput
            testid="bp-escalation"
            label="Human contact / escalation number"
            value={form.escalation_phone}
            onChange={(v) => set("escalation_phone", v)}
          />
          <span className="-mt-2 block text-[11px] text-neutral-400">Offered when a caller needs a person.</span>
        </Section>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-4">
          {dirty && <span className="mr-auto text-[11px] text-amber-600" data-testid="bp-unsaved">Unsaved changes</span>}
          <Btn variant="ghost" data-testid="bp-cancel" disabled={!dirty || saving} onClick={() => { setForm(original); setErrors({}); }}>Cancel</Btn>
          <Btn data-testid="bp-save" disabled={saving || !dirty} onClick={save}>{saving ? "Saving…" : "Save business profile"}</Btn>
        </div>
      </div>
    </Card>
  );
}
